/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import moment from 'moment';

import { Description } from '../../../../../../styledComponents/styledComponents';
import {
   AlertMarker,
   ConnectionLabel,
   ConnectionRow,
   DpMarker,
   LegendColorBox,
   LegendContainer,
   LegendItem,
   LegendLabel,
   SegmentFill,
   TimelineBar,
   TimelineSectionWrapper,
} from './AlertWidgetStyles';
import { CHRONOLOGICAL_DATE_TIME_FORMAT } from '../../../constants';

const DISABLED_COLOR = 'rgb(220, 220, 220)';
const DISABLED_STYLE = { opacity: 0.6, pointerEvents: 'none' };

function TimelineSection({
   connections,
   rangeStart,
   totalDuration,
   alertStart,
   alertEnd,
   alertStartPercent,
   alertEndPercent,
}) {
   const [visible, setVisible] = useState({
      active: true,
      inactive: true,
      terminated: true,
      alertMarkers: true,
   });

   const toggle = (key) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

   const legendItems = [
      { color: 'rgb(75, 192, 75)', label: 'Active', key: 'active' },
      { color: 'rgb(220, 75, 75)', label: 'Inactive', key: 'inactive' },
      { color: 'rgb(30, 30, 30)', label: 'Terminated', key: 'terminated' },
      { color: 'rgb(60, 120, 220)', label: 'Alert Start / End', key: 'alertMarkers' },
   ];

   const toPercent = (ms) => ((ms - rangeStart) / totalDuration) * 100;

   const alertMarkers = [
      { label: 'Alert Start', moment: alertStart, left: alertStartPercent },
      { label: 'Alert End', moment: alertEnd, left: alertEndPercent },
   ];

   return (
      <TimelineSectionWrapper>
         <Description marginLeftPx={0}>Activity Timeline</Description>
         {connections.map(({ connectionId: connId, blocks, terminated, terminatedAt }) => {
            return (
               <ConnectionRow key={connId} $marginTopPx={8}>
                  <ConnectionLabel>{'Connection ID: ' + connId}</ConnectionLabel>
                  <TimelineBar>
                     {alertMarkers.map(({ label, moment: m, left }) => (
                        <AlertMarker
                           key={label}
                           {...(visible.alertMarkers && {
                              'data-tooltip-id': 'timeline-tooltip',
                              'data-tooltip-html': ReactDOMServer.renderToStaticMarkup(
                                 <div>
                                    <div>
                                       <b>{label}</b>
                                    </div>
                                    <div>{m.format(CHRONOLOGICAL_DATE_TIME_FORMAT)}</div>
                                 </div>,
                              ),
                           })}
                           style={{
                              left: `${left}%`,
                              ...(!visible.alertMarkers && { backgroundColor: DISABLED_COLOR, ...DISABLED_STYLE }),
                           }}
                        />
                     ))}
                     {blocks.map((block) => {
                        const startMs = block.startTimestamp * 1000;
                        const endMs = block.endTimestamp * 1000;
                        const startPercent = toPercent(startMs);
                        const widthPercent = toPercent(endMs) - toPercent(startMs);
                        const isZeroDuration = widthPercent === 0;
                        const isDisabled = block.isActive ? !visible.active : !visible.inactive;
                        const color = block.isActive ? 'rgb(75, 192, 75)' : 'rgb(220, 75, 75)';
                        const label = block.isActive ? 'Active' : 'Inactive';
                        return (
                           <SegmentFill
                              key={`${connId}-${block.startTimestamp}`}
                              $color={isDisabled ? DISABLED_COLOR : color}
                              {...(!isDisabled && {
                                 'data-tooltip-id': 'timeline-tooltip',
                                 'data-tooltip-html': ReactDOMServer.renderToStaticMarkup(
                                    <div>
                                       <div>
                                          <b>{'ID: ' + connId}</b>
                                       </div>
                                       <div>{label}</div>
                                       {isZeroDuration ? (
                                          <div>{`At: ${moment.utc(startMs).format(CHRONOLOGICAL_DATE_TIME_FORMAT)}`}</div>
                                       ) : (
                                          <>
                                             <div>{`From: ${moment.utc(startMs).format(CHRONOLOGICAL_DATE_TIME_FORMAT)}`}</div>
                                             <div>{`To: ${moment.utc(endMs).format(CHRONOLOGICAL_DATE_TIME_FORMAT)}`}</div>
                                          </>
                                       )}
                                    </div>,
                                 ),
                              })}
                              style={{
                                 left: `${startPercent}%`,
                                 width: isZeroDuration ? '3px' : `${widthPercent}%`,
                                 ...(isZeroDuration && { transform: 'translateX(-50%)' }),
                                 ...(isDisabled && DISABLED_STYLE),
                              }}
                           />
                        );
                     })}
                     {terminated && (
                        <DpMarker
                           $color={visible.terminated ? 'rgb(30, 30, 30)' : DISABLED_COLOR}
                           {...(visible.terminated && {
                              'data-tooltip-id': 'timeline-tooltip',
                              'data-tooltip-html': ReactDOMServer.renderToStaticMarkup(
                                 <div>
                                    <div>
                                       <b>{'ID: ' + connId}</b>
                                    </div>
                                    <div>{'Connection terminated'}</div>
                                    <div>{moment.utc(terminatedAt * 1000).format(CHRONOLOGICAL_DATE_TIME_FORMAT)}</div>
                                 </div>,
                              ),
                           })}
                           style={{
                              left: `${toPercent(terminatedAt * 1000)}%`,
                              ...(!visible.terminated && DISABLED_STYLE),
                           }}
                        />
                     )}
                  </TimelineBar>
               </ConnectionRow>
            );
         })}
         <LegendContainer>
            {legendItems.map(({ color, label, key }) => (
               <LegendItem key={label} style={{ cursor: 'pointer' }} onClick={() => toggle(key)}>
                  <LegendColorBox
                     $color={visible[key] ? color : DISABLED_COLOR}
                     style={!visible[key] ? { opacity: 0.4 } : {}}
                  />
                  <LegendLabel>{label}</LegendLabel>
               </LegendItem>
            ))}
         </LegendContainer>
         <ReactTooltip
            id='timeline-tooltip'
            place={'top'}
            className={'reduced-line-height bring-on-top'}
            backgroundColor={'rgba(0,0,0,0.9)'}
            textColor={'rgb(240,240,240)'}
            style={{ textAlign: 'center' }}
         />
      </TimelineSectionWrapper>
   );
}

TimelineSection.propTypes = {
   connections: PropTypes.array.isRequired,
   rangeStart: PropTypes.number.isRequired,
   totalDuration: PropTypes.number.isRequired,
   alertStart: PropTypes.object.isRequired,
   alertEnd: PropTypes.object.isRequired,
   alertStartPercent: PropTypes.number.isRequired,
   alertEndPercent: PropTypes.number.isRequired,
};

export default TimelineSection;
